import { useQuery } from '@apollo/client'
import * as Sentry from '@sentry/browser'
import { Channel } from 'actioncable'
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState /* all your hooks */
} from 'react'

import consumer from '../../cable'
import { ACTIONS_QUERY, USER_STATS_QUERY } from '../../graphql/queries'
import { ActionCableSubscription, UserChannelMessage } from '../../utils/fixtures'
import {
  convertKeysToCamelCase,
  getClient,
  getToken,
  removeUser as removeStorageUser,
  setUser as setStorageUser
} from '../../utils/helper'
import {
  defaultActionsCallback,
  defaultContext,
  defaultDraftInvoiceCallback,
  initialValues,
  UserContext
} from './UserContext'

interface UserContextProviderProps {
  children: React.ReactNode
}

export const UserContextProvider: React.FC<UserContextProviderProps> = ({ children }) => {
  const [user, setLocalUser] = useState(() => defaultContext.user)

  const isLegalAdvisor = useMemo(() => ['legal_advisor'].includes(user?.role), [user?.role])
  const isEditor = useMemo(() => ['user', 'legal_advisor'].includes(user?.role), [user?.role])
  const isAdmin = useMemo(() => ['admin'].includes(user?.role), [user?.role])
  const isCreator = useMemo(() => ['creator'].includes(user?.role), [user?.role])
  const isEmployeeHohner = user?.employeeHohner === true
  const reportingEnabled = user?.reportingEnabled === true
  const isExternal = useMemo(
    () => ![...new Set([isAdmin, isEditor, isCreator, isLegalAdvisor])].includes(true),
    [isAdmin, isEditor, isCreator, isLegalAdvisor]
  )

  const [processingId, setProcessingId] = useState<string | undefined>(initialValues.processingId)
  const [isEditorModalOpen, setIsEditorModalOpen] = useState(false)

  const {
    data: userStatsData,
    loading: loadingUserStats,
    error: errorUserStats,
    refetch: refetchUserStats
  } = useQuery(USER_STATS_QUERY, {
    fetchPolicy: 'network-only',
    skip: !user?.id,
    ssr: false,
    variables: {
      isLegalAdvisor: isAdmin || isLegalAdvisor,
      isAdmin
    }
  })

  // Verwenden eines Refs, um den Callback zu speichern
  const draftInvoiceCallbackRef = useRef<(data: UserChannelMessage) => void>(
    defaultDraftInvoiceCallback
  )

  const setDraftInvoiceCallback = useCallback((callback: (data: UserChannelMessage) => void) => {
    if (typeof callback === 'function') {
      draftInvoiceCallbackRef.current = callback
    }
  }, [])

  // Diese Funktion ruft immer den aktuell im Ref gespeicherten Callback auf.
  const handleDraftInvoiceGenerated = useCallback((data: UserChannelMessage) => {
    draftInvoiceCallbackRef.current(data)
  }, [])

  const actionsCallbackRef = useRef<(data: UserChannelMessage) => void>(defaultActionsCallback)

  const setActionsCallback = useCallback((callback: (data: UserChannelMessage) => void) => {
    if (typeof callback === 'function') {
      actionsCallbackRef.current = callback
    }
  }, [])

  const handleActionsCallback = useCallback((data: UserChannelMessage) => {
    actionsCallbackRef.current(data)
  }, [])
  useEffect(() => {
    let subscription: Channel | undefined
    const token = getToken()
    const client = getClient()
    const params = {
      channel: 'UserChannel',
      user_id: `${user?.id}`,
      token,
      client
    }
    if (!subscription && user?.id) {
      console.debug('[*] creating user subscription')
      subscription = consumer.subscriptions.create(params, {
        connected: () => {
          console.debug('[+] ActionCable - CONNECTED')
        },
        disconnected: () => {
          console.debug('[-] ActionCable - DISCONNECTED')
        },
        received: (data: UserChannelMessage) => {
          console.debug('[+] ActionCable - received data: ', data)
          switch (data.action) {
            case 'call-accepted':
              console.debug('user', user)
              console.debug('data', data)
              console.debug('data.m2_case_id', data.m2_case_id)
              console.debug('data.processing_id', data.processing_id)

              const redirectUrl = isEditor
                ? `/cases/${data.m2_case_id}/processing/${data.processing_id}/?tab=processing&action=${data.action_id}`
                : `/cases/${data.m2_case_id}/processing/${data.processing_id}/?subTab=processing&tab=processing-${data.processing_id}&action=${data.action_id}`

              if (data.open_new_tab) {
                window.open(redirectUrl, '_blank', 'noopener,noreferrer')
              } else {
                window.location.href = redirectUrl
              }

              break
            case 'call-accepted-existing-processing':
              // note: open latest existing processing in new tab
              window.open(
                `/cases/${data.m2_case_id}/processing/${data.processing_id}/?tab=processing`,
                '_blank',
                'noopener,noreferrer'
              )
              break
            case 'user-stats':
              refetchUserStats()
              break
            case 'draft-invoice-generated':
              handleDraftInvoiceGenerated(data)
              break
            case 'new-notification':
              window.dispatchEvent(
                new CustomEvent('m2:new-notification', { detail: data.notification })
              )
              break
            case 'notifications-read':
              // note: another user resolved the underlying issue — our copy of
              // the notification was marked as read server-side
              window.dispatchEvent(new CustomEvent('m2:notifications-read', { detail: data }))
              break

            default:
              handleActionsCallback(data)
              break
          }
        }
      })
      console.debug('subscriptions', consumer.subscriptions)
    }

    return () => {
      console.debug(`[-] ActionCable - Unmounting UserContext: ${subscription}`)
      subscription?.unsubscribe()
    }
  }, [
    user?.id,
    refetchUserStats,
    user,
    isEditor,
    handleDraftInvoiceGenerated,
    handleActionsCallback
  ])

  useEffect(() => {
    const token = getToken()
    const client = getClient()
    const params = {
      channel: 'AdminChannel',
      admin_id: `${user?.id}`,
      token,
      client
    }
    if (
      isAdmin &&
      user?.id &&
      !consumer.subscriptions.subscriptions.find(
        (sub: ActionCableSubscription) => sub.identifier === JSON.stringify(params)
      )
    ) {
      console.debug('[*] creating admin subscription')
      consumer.subscriptions.create(params, {
        connected: () => {
          console.debug('[+] ActionCable - AdminChannel - CONNECTED')
        },
        disconnected: () => {
          console.debug('[-] ActionCable - AdminChannel - DISCONNECTED')
        },
        received: (data: UserChannelMessage) => {
          console.debug('[+] ActionCable - AdminChannel - received data: ', data)
          if (data?.action === 'user-stats') {
            refetchUserStats()
          }
        }
      })
      console.debug('subscriptions', consumer.subscriptions)
    }
    return () => {
      if (isAdmin) {
        const sub = consumer.subscriptions.subscriptions?.find(
          (s: ActionCableSubscription) => s.identifier === JSON.stringify(params)
        )
        console.debug('[-] Unmounting AdminChannel - UserContext')
        sub?.unsubscribe()
      }
    }
  }, [user?.id, refetchUserStats, isAdmin])

  const {
    data: actionsData,
    loading: loadingActions,
    error: errorActions,
    refetch: refetchActions
  } = useQuery(ACTIONS_QUERY, {
    skip: !processingId || processingId.length <= 0 || !user?.id,
    variables: {
      processingId,
      sortBy: {
        attribute: 'date',
        direction: 'desc'
      }
    },
    fetchPolicy: 'cache-and-network'
  })

  useEffect(() => {
    if (processingId && !isEditorModalOpen) {
      refetchActions()
    }
  }, [processingId, refetchActions, isEditorModalOpen])

  const [userStats, setUserStats] = useState(undefined)
  useEffect(() => {
    if (userStatsData?.userStats && !isEditorModalOpen) {
      setUserStats(userStatsData?.userStats)
    }
  }, [userStatsData, isEditorModalOpen])

  const [actions, setActions] = useState([])

  useEffect(() => {
    // Always update actions when data changes
    // The isEditorModalOpen check was causing issues where new actions
    // created in a modal wouldn't appear until page reload
    if (actionsData?.actions?.collection) {
      setActions(actionsData?.actions?.collection)
    }
  }, [actionsData])

  const updateUser = useCallback((userData: any) => {
    if (userData) {
      const camelCaseUserData = convertKeysToCamelCase(userData)
      Sentry.setUser({ email: userData.email })
      setStorageUser(camelCaseUserData)
      setLocalUser((usr: any) => ({
        ...usr,
        ...camelCaseUserData,
        role: userData?.role,
        name: [userData.firstname, userData.lastname].join(' ')
      }))
    }
  }, [])

  const removeUser = () => {
    Sentry.setUser(null)
    removeStorageUser()
    setLocalUser(initialValues)
  }

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: updateUser,
        removeUser,
        isEditor,
        isAdmin,
        isCreator,
        isExternal,
        isLegalAdvisor,
        isEmployeeHohner,
        reportingEnabled,
        processingId,
        setProcessingId,
        loadingActions,
        loadingUserStats,
        errorActions,
        errorUserStats,
        actions,
        userStats,
        refetchActions,
        refetchUserStats,
        isEditorModalOpen,
        setIsEditorModalOpen,
        handleDraftInvoiceGenerated,
        setDraftInvoiceCallback,
        handleActionsCallback,
        setActionsCallback
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

export default UserContextProvider
